import * as React from 'react';

import { LucideCheckCircle, LucideLoader2 } from 'lucide-react';
import {
  Button as AriaButton,
  composeRenderProps,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';

import { useTranslations } from '../../../i18n/useTranslations';
import { cn, withColor } from '../../../lib';
import { ThemeColor } from '../../../types/theme';

type TouchButtonContentProps = {
  children: React.ReactNode;
  highlightContent?: string;
  isPending?: boolean;
  isSaved?: boolean;
};

// ⚛️ ⚛️ ⚛️ TOUCH BUTTON CONTENT ⚛️ ⚛️ ⚛️

const TouchButtonContent: React.FC<TouchButtonContentProps> = ({
  children,
  highlightContent,
  isPending,
  isSaved,
}) => {
  const t = useTranslations();

  if (isPending) {
    return (
      <>
        <LucideLoader2 className="size-5 animate-spin text-neutral/60" />
        <span className="text-sm font-medium text-neutral/70">
          {t.components.saving}
        </span>
      </>
    );
  }

  if (isSaved) {
    return (
      <>
        <LucideCheckCircle className="size-5 text-success" />
        <span className="text-sm font-medium text-success">
          {t.components.saved}
        </span>
      </>
    );
  }

  return (
    <>
      {children}
      {highlightContent && (
        <div className="ml-auto flex items-center justify-center rounded-md bg-neutral p-1.5">
          <span className="text-lg text-neutral/70">{highlightContent}</span>
        </div>
      )}
    </>
  );
};

export type TouchButtonProps = {
  children: React.ReactNode;
  isSelected?: boolean;
  className?: string;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  highlightContent?: string;
  style?: React.CSSProperties;
  variant?: 'solid' | 'outline' | 'subtle' | 'ghost' | 'text';
  color?: ThemeColor;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  align?: 'center' | 'left';
  isPending?: boolean;
  isSaved?: boolean;
  onPress: () => void;
};

type FullTouchButtonProps = TouchButtonProps &
  Omit<AriaButtonProps, 'children' | 'className' | 'style' | 'onPress'> & {
    ref?: React.Ref<HTMLButtonElement>;
  };

// ⚛️ ⚛️ ⚛️ TOUCH BUTTON ⚛️ ⚛️ ⚛️

export const TouchButton: React.FC<FullTouchButtonProps> = ({
  children,
  className,
  leftElement,
  rightElement,
  isSelected,
  highlightContent,
  style,
  variant = 'solid',
  color,
  size = 'md',
  align = 'center',
  isPending,
  isSaved,
  onPress,
  ref,
  ...props
}) => (
  <AriaButton
    className={composeRenderProps(className, (className) =>
      cn(
        '@TouchButton',
        'flex h-[55px] items-center gap-2.5 px-3',
        'cursor-pointer rounded-md outline-none',
        'border border-solid border-neutral/20 bg-transparent text-base',

        align === 'left' && 'justify-start',
        align !== 'left' && 'justify-center',

        'rac-hover:border-primary rac-hover:text-primary',
        'rac-disabled:pointer-events-none rac-disabled:opacity-50',

        isPending && 'border-neutral/30 bg-white',
        isSaved && 'border-success bg-success/5',
        isSelected && 'border-primary bg-primary text-white',

        variant === 'solid' &&
          'border-custom-color bg-custom-color text-white rac-hover:border-custom-color/80 rac-hover:bg-custom-color/80 rac-hover:text-white rac-pressed:bg-custom-color',

        variant === 'outline' &&
          'border-2 border-custom-color/20 bg-white text-custom-color rac-hover:border-custom-color/80 rac-hover:bg-custom-color/5 rac-hover:text-custom-color/80 rac-pressed:border-custom-color rac-pressed:text-custom-color',
        variant === 'outline' &&
          color === 'neutral' &&
          '!border-custom-color/20 !text-custom-color',
        variant === 'outline' &&
          color === 'primary' &&
          'border-custom-color text-custom-color',
        variant === 'outline' &&
          isSelected &&
          '!border-transparent border-primary bg-primary text-white',

        variant === 'subtle' &&
          'border-neutral/20 text-neutral/60 rac-hover:border-primary rac-hover:text-primary',
        variant === 'subtle' && isSelected && 'border-primary text-white',
        variant === 'subtle' &&
          color === 'primary' &&
          isSelected &&
          'border-custom-color text-custom-color',
        variant === 'subtle' &&
          color === 'primary' &&
          'border-transparent text-neutral/60',

        variant === 'ghost' && 'border-transparent',
        variant === 'ghost' &&
          !color &&
          'active:bg-neutral/20 rac-hover:bg-neutral/10 rac-hover:text-neutral/80',
        variant === 'ghost' &&
          color &&
          'text-custom-color active:bg-custom-color/20 rac-hover:border-transparent rac-hover:bg-custom-color/10 rac-hover:text-custom-color/80',
        variant === 'text' &&
          '!border-transparent bg-transparent text-custom-color rac-hover:border-transparent rac-hover:bg-custom-color/10 rac-hover:text-custom-color/80 rac-pressed:bg-custom-color/20 rac-pressed:text-custom-color',

        size === 'sm' && 'h-9 min-h-9 gap-1.5 px-2 text-sm',
        size === 'lg' && 'h-11 min-h-11',
        size === 'xl' && 'h-13 min-h-13',
        className,
      ),
    )}
    style={{ ...withColor(color ?? 'neutral'), ...style }}
    onPress={onPress}
    ref={ref}
    {...props}
  >
    {leftElement}
    <div
      className={cn(
        'flex items-center truncate text-center font-medium',
        align === 'left' && 'w-0 flex-grow text-left',
        (isPending || isSaved) && 'gap-2',
      )}
    >
      <TouchButtonContent
        isPending={isPending}
        isSaved={isSaved}
        highlightContent={highlightContent}
      >
        {children}
      </TouchButtonContent>
    </div>
    {rightElement}
  </AriaButton>
);
